import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/Auth";
import { useParams } from "react-router-dom";
import Loading from "../../../components/Loading/Loading";
import Swal from "sweetalert2";
import {
  Users,
  Building2,
  User,
  Phone,
  MapPin,
  CreditCard,
  Landmark,
  Save,
  ArrowLeft,
  Edit3,
  DollarSign,
} from "lucide-react";

const EditSupplier = () => {
  const { requestWithAuth } = useAuth();
  const { supplier_id } = useParams();
  const [supplier, setSupplier] = useState({});
  const [newSupplierData, setNewSupplierData] = useState({});
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleChange = (e) => {
    setNewSupplierData({ ...newSupplierData, [e.target.name]: e.target.value });
  };

  const updateSupplier = async (e) => {
    e.preventDefault();
    setCreatingSupplier(true);
    const body = JSON.stringify(newSupplierData);
    try {
      const response = await requestWithAuth(
        `/supplier/${supplier_id}/details/`,
        "PUT",
        body,
        {
          "Content-Type": "application/json",
        }
      );
      setCreatingSupplier(false);
      Swal.fire({
        title: "Success",
        icon: "success",
        text: "Supplier Updated Successfully",
      }).then(() => {
        window.location.href = "/administration/suppliers";
      });
    } catch (error) {
      toast.error("Failed to update supplier. Please try again later.");
      console.error(error);
      setCreatingSupplier(false);
    }
  };

  const getSupplier = async (e) => {
    setLoading(true);

    try {
      const response = await requestWithAuth(
        `/supplier/${supplier_id}/details/`,
        "GET"
      );
      setLoading(false);
      setNewSupplierData(response);
    } catch (error) {
      toast.error("Failed to get supplier details. Please try again later.");
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getSupplier();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => (window.location.href = "/administration/suppliers")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Suppliers</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Administration</span>
                <span>/</span>
                <span>Suppliers</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Edit</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Edit3 size={32} className="text-blue-600" />
                Edit Supplier
              </h1>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Supplier Information
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Update supplier details and contact information
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Basic Information Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <Building2 size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Basic Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Supplier Name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Building2 size={16} className="text-gray-500" />
                        Supplier Name
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={newSupplierData?.name || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter supplier name"
                      />
                    </div>

                    {/* Supplier Address */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <MapPin size={16} className="text-gray-500" />
                        Supplier Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={newSupplierData?.address || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter address"
                      />
                    </div>
                  </div>
                </div>

                {/* Banking Information Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <Landmark size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Banking Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Bank Name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Landmark size={16} className="text-gray-500" />
                        Bank Name
                      </label>
                      <input
                        type="text"
                        name="bank"
                        value={newSupplierData?.bank || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter bank name"
                      />
                    </div>

                    {/* Account Number */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <CreditCard size={16} className="text-gray-500" />
                        Account Number
                      </label>
                      <input
                        type="text"
                        name="account_number"
                        value={newSupplierData?.account_number || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter account number"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <User size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Contact Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Contact Person */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <User size={16} className="text-gray-500" />
                        Contact Person
                      </label>
                      <input
                        type="text"
                        name="contact_person"
                        value={newSupplierData?.contact_person || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter contact person name"
                      />
                    </div>

                    {/* Contact Number */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Phone size={16} className="text-gray-500" />
                        Contact Person Number
                      </label>
                      <input
                        type="text"
                        name="contact_person_number"
                        value={newSupplierData?.contact_person_number || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>
                </div>

                {/* Financial Information Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <DollarSign size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Financial Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Opening Balance */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <DollarSign size={16} className="text-gray-500" />
                        Opening Balance
                      </label>
                      <input
                        type="number"
                        name="opening_balance"
                        min="0"
                        step="0.01"
                        value={newSupplierData?.opening_balance ?? 0}
                        onChange={handleChange}
                        onWheel={(e) => e.target.blur()}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="0.00"
                      />
                      <p className="mt-1 text-xs text-gray-500">Initial balance owed to this supplier</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      (window.location.href = "/administration/suppliers")
                    }
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  {creatingSupplier ? (
                    <button
                      type="button"
                      disabled
                      className="bg-blue-400 text-white rounded-lg px-6 py-3 flex items-center gap-2 cursor-not-allowed"
                    >
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={updateSupplier}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 font-medium transition-colors shadow-sm flex items-center gap-2"
                    >
                      <Save size={18} />
                      Update Supplier
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Help Card */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-1">
                  Editing Supplier Information
                </h3>
                <p className="text-xs text-blue-700">
                  Update the supplier's details as needed. Required fields are
                  marked with an asterisk (*). Changes will be saved immediately
                  after submission.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSupplier;
